// プレビューペイン操作関連のハンドラー
import { savePreviewHeight, restorePreviewHeight } from '../state/app-state.js';

/**
 * プレビューペインの高さを保存
 */
export function savePreviewHeightLocal(): void {
    if (typeof window === 'undefined' || !window.localStorage) return;
    const previewPane = document.getElementById('previewPane');
    if (!previewPane) return;
    try {
        const height = previewPane.offsetHeight || 0;
        savePreviewHeight(height);
    } catch (e) {
        console.warn('プレビューペインの高さの保存に失敗しました:', e);
    }
}

/**
 * プレビューペインの高さを復元（スマホのみ）
 */
export function restorePreviewHeightLocal(): void {
    if (typeof window === 'undefined' || !window.localStorage) return;
    const previewPane = document.getElementById('previewPane');
    if (!previewPane) return;
    
    // デスクトップ表示では復元しない
    if (window.matchMedia('(min-width: 768px)').matches) {
        // デスクトップ表示では、スタイルをリセット
        previewPane.style.flex = '';
        previewPane.style.flexShrink = '';
        previewPane.style.height = '';
        previewPane.style.marginTop = '';
        return;
    }
    
    try {
        const savedHeight = restorePreviewHeight();
        if (savedHeight !== null) {
            // flex-1を無効化して固定高さにする
            previewPane.style.flex = 'none';
            previewPane.style.flexShrink = '0';
            previewPane.style.height = `${savedHeight}px`;
        }
    } catch (e) {
        console.warn('プレビューペインの高さの復元に失敗しました:', e);
    }
}

/**
 * プレビューペインのリサイズを開始
 */
export function startPreviewResize(e: MouseEvent | TouchEvent): void {
    e.preventDefault();
    e.stopPropagation();
    const previewPane = document.getElementById('previewPane');
    if (!previewPane) return;
    
    const container = previewPane.parentElement;
    if (!container) return;
    const containerRect = container.getBoundingClientRect();
    const touches = (e as TouchEvent).touches;
    const startY = touches ? (touches[0]?.clientY ?? 0) : (e as MouseEvent).clientY;
    const paneRect = previewPane.getBoundingClientRect();
    const startPaneTop = paneRect.top;
    const startPaneHeight = previewPane.offsetHeight || paneRect.height;
    const startPaneBottom = startPaneTop + startPaneHeight;
    
    // 親要素を基準にした相対位置を計算
    const startPaneTopRelative = startPaneTop - containerRect.top;
    const startPaneLeftRelative = paneRect.left - containerRect.left;
    
    // ハンドルの中心位置を取得
    const resizeHandle = document.getElementById('previewResizeHandle');
    const handleHeight = resizeHandle ? resizeHandle.offsetHeight : 32;
    const handleCenterOffset = handleHeight / 2;
    
    // 開始時のハンドル中心位置と指の位置の関係を記録
    const startHandleCenterY = startPaneTop + handleCenterOffset;
    const startYOffset = startY - startHandleCenterY;
    
    const minHeight = 200;
    const maxHeight = Math.min(window.innerHeight * 0.8, containerRect.height - 100);
    const minTop = 100;
    const maxTop = containerRect.height - minHeight;
    
    // 親要素をposition: relativeにする（absoluteの基準にするため）
    const originalContainerPosition = container.style.position;
    if (!container.style.position || container.style.position === 'static') {
        container.style.position = 'relative';
    }
    
    // flex-1を無効化して固定高さにする
    previewPane.style.flex = 'none';
    previewPane.style.flexShrink = '0';
    previewPane.style.height = `${startPaneHeight}px`;
    previewPane.style.position = 'absolute'; // ドラッグ中はabsoluteにして元の位置に空白を残さない
    previewPane.style.top = `${startPaneTopRelative}px`;
    previewPane.style.left = `${startPaneLeftRelative}px`;
    previewPane.style.width = `${paneRect.width}px`;
    previewPane.style.zIndex = '10'; // ドラッグ中は右ペインを前面に
    
    const handleMove = (moveEvent: MouseEvent | TouchEvent) => {
        moveEvent.preventDefault();
        const touches = (moveEvent as TouchEvent).touches;
        // TouchEventの場合はtouches[0]?.clientY、MouseEventの場合はclientYを使用
        const currentY = touches ? touches[0]?.clientY : (moveEvent as MouseEvent).clientY;
        // TouchEventの場合のみundefinedの可能性があるためチェック
        if (currentY === undefined) return;
        
        // 指の位置からハンドル中心の目標位置を計算
        const targetHandleCenterY = currentY - startYOffset;
        
        // ハンドル中心位置から右ペインの上端位置を計算（ビューポート基準）
        let newTopViewport = targetHandleCenterY - handleCenterOffset;
        newTopViewport = Math.max(containerRect.top + minTop, Math.min(containerRect.bottom - minHeight, newTopViewport));
        
        // 親要素を基準にした相対位置に変換
        let newTopRelative = newTopViewport - containerRect.top;
        newTopRelative = Math.max(minTop, Math.min(maxTop, newTopRelative));
        
        // 高さを計算
        const newHeight = startPaneBottom - newTopViewport;
        const clampedHeight = Math.max(minHeight, Math.min(maxHeight, newHeight));
        
        // 右ペインの位置と高さを設定
        previewPane.style.top = `${newTopRelative}px`;
        previewPane.style.height = `${clampedHeight}px`;
    };
    
    const handleEnd = () => {
        // 親要素のpositionを元に戻す
        container.style.position = originalContainerPosition;
        
        // デスクトップ表示ではスタイルをリセット
        if (window.matchMedia('(min-width: 768px)').matches) {
            previewPane.style.flex = '';
            previewPane.style.flexShrink = '';
            previewPane.style.height = '';
            previewPane.style.position = '';
            previewPane.style.top = '';
            previewPane.style.left = '';
            previewPane.style.width = '';
            previewPane.style.zIndex = '';
        } else {
            // スマホ表示では、高さのみを保持してpositionをリセット
            const finalHeight = previewPane.offsetHeight;
            previewPane.style.height = `${finalHeight}px`;
            previewPane.style.position = ''; // positionをリセット
            previewPane.style.top = '';
            previewPane.style.left = '';
            previewPane.style.width = '';
            previewPane.style.zIndex = ''; // z-indexをリセット
            savePreviewHeightLocal();
        }
        
        document.removeEventListener('mousemove', handleMove);
        document.removeEventListener('mouseup', handleEnd);
        document.removeEventListener('touchmove', handleMove);
        document.removeEventListener('touchend', handleEnd);
    };
    
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleEnd);
    document.addEventListener('touchmove', handleMove, { passive: false });
    document.addEventListener('touchend', handleEnd);
}

